<script setup lang="ts">
import { Volume2 } from 'lucide-vue-next'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { CSSProperties } from 'vue'

import {
  FLASHCARD_AUTOPLAY_AUDIO,
  FLASHCARD_DISABLE_AUTOPLAY,
  FLASHCARD_FORCE_AUDIO_VISIBLE,
  FLASHCARD_FORCE_HIDE_AUDIO,
  FLASHCARD_HIDE_AUDIO,
  FLASHCARD_INTERACTIVE,
  FLASHCARD_VISIBLE_FACE,
} from '../../flashcard/context'

const props = defineProps<{
  src: string
  autoplay: boolean
  notVisible: boolean
  loop: boolean
  muted: boolean
  preload: 'none' | 'metadata' | 'auto'
  face: 'front' | 'back'
  style?: CSSProperties
  iconSizePx?: number
}>()

const isInteractive = inject(
  FLASHCARD_INTERACTIVE,
  computed(() => true),
)
const isCanvas = computed(() => isInteractive.value === false)

const autoplayOverride = inject(
  FLASHCARD_AUTOPLAY_AUDIO,
  computed(() => false),
)

const disableAutoplay = inject(
  FLASHCARD_DISABLE_AUTOPLAY,
  computed(() => false),
)

const hideOverride = inject(
  FLASHCARD_HIDE_AUDIO,
  computed(() => false),
)

const forceVisible = inject(
  FLASHCARD_FORCE_AUDIO_VISIBLE,
  computed(() => false),
)

const forceHideOverride = inject(
  FLASHCARD_FORCE_HIDE_AUDIO,
  computed(() => false),
)

const visibleFace = inject(
  FLASHCARD_VISIBLE_FACE,
  computed(() => 'front' as const),
)

const shouldAutoplay = computed(() => {
  if (isCanvas.value) return false
  if (disableAutoplay.value) return false
  // Be strict about booleans: avoid cases like "false" (string) becoming truthy.
  return props.autoplay === true || autoplayOverride.value === true
})

const isHidden = computed(() => {
  if (forceVisible.value) return false

  // Unconditional hide (e.g. flowchartHideAudio toggle) — no autoplay requirement.
  if (forceHideOverride.value === true) return true

  // Autoplay-dependent hide: the button is hidden so audio plays silently.
  if (!shouldAutoplay.value) return false
  if (hideOverride.value === true) return true
  return props.notVisible === true
})

const isFaceVisible = computed(() => visibleFace.value === props.face)

const isAutoplayActive = computed(() => shouldAutoplay.value && isFaceVisible.value)

const audioRef = ref<HTMLAudioElement | null>(null)
const isPlaying = ref(false)

const updatePlayingState = () => {
  const el = audioRef.value
  if (!el) {
    isPlaying.value = false
    return
  }

  isPlaying.value = !el.paused && !el.ended
}

const replayFromStart = async () => {
  const el = audioRef.value
  if (!el) return

  try {
    el.currentTime = 0
  } catch {
    // Some streams disallow seeking.
  }

  try {
    await el.play()
  } catch {
    // Autoplay policies / missing media.
  }
  updatePlayingState()
}

onMounted(() => {
  // Ensure autoplay works on the first card even if the initial watcher fires
  // before the template ref is populated.
  if (isAutoplayActive.value) {
    void replayFromStart()
  }
})

const handlePointerDown = (event: PointerEvent) => {
  if (isCanvas.value) return
  if (isHidden.value) return
  event.preventDefault()
  event.stopPropagation()
}

const handleClick = async (event: MouseEvent) => {
  if (isCanvas.value) return
  if (isHidden.value) return

  event.preventDefault()
  event.stopPropagation()

  await replayFromStart()
}

const wrapperStyle = computed<CSSProperties>(() => ({
  ...(props.style ?? {}),
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '999px',
  background: 'rgba(15, 23, 42, 0.65)',
  boxShadow: '0 14px 40px rgba(0, 0, 0, 0.35)',
  overflow: 'hidden',
  ...(isHidden.value
    ? {
        opacity: 0,
        pointerEvents: 'none',
        background: 'transparent',
        boxShadow: 'none',
      }
    : null),
}))

const iconStyle = computed<CSSProperties>(() => {
  const size = typeof props.iconSizePx === 'number' && props.iconSizePx > 0 ? props.iconSizePx : 14
  return { width: `${size}px`, height: `${size}px` }
})

onMounted(() => {
  const el = audioRef.value
  if (!el) return
  el.addEventListener('play', updatePlayingState)
  el.addEventListener('pause', updatePlayingState)
  el.addEventListener('ended', updatePlayingState)
  updatePlayingState()
})

watch(
  isAutoplayActive,
  (next, prev) => {
    // Only autoplay when the element *becomes* visible.
    if (!next || prev) return
    void replayFromStart()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => props.src,
  () => {
    // If src arrives/changes after the initial autoplay trigger, retry.
    if (!isAutoplayActive.value) return
    void replayFromStart()
  },
  { flush: 'post' },
)

watch(
  isFaceVisible,
  (visible) => {
    if (visible) return

    const el = audioRef.value
    if (!el) return

    try {
      el.pause()
    } catch {
      // ignore
    }

    // Reset so autoplay restarts from the beginning when the face becomes visible again.
    try {
      el.currentTime = 0
    } catch {
      // Some streams disallow seeking.
    }

    updatePlayingState()
  },
  { immediate: true, flush: 'post' },
)

onBeforeUnmount(() => {
  const el = audioRef.value
  if (!el) return
  el.removeEventListener('play', updatePlayingState)
  el.removeEventListener('pause', updatePlayingState)
  el.removeEventListener('ended', updatePlayingState)
})
</script>

<template>
  <div
    class="audio-shell"
    :style="wrapperStyle"
    :data-playing="isPlaying ? 'true' : 'false'"
    :aria-hidden="isHidden ? 'true' : 'false'"
    @pointerdown.capture="handlePointerDown"
    @click.capture="handleClick"
  >
    <audio
      ref="audioRef"
      class="audio-media"
      :src="isCanvas ? undefined : props.src"
      :loop="props.loop"
      :muted="props.muted"
      :preload="isCanvas ? 'none' : props.preload"
    />

    <Volume2 class="audio-icon" :style="iconStyle" aria-hidden="true" />
  </div>
</template>

<style scoped>
.audio-media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  pointer-events: none;
}

.audio-icon {
  color: rgba(248, 250, 252, 0.95);
  filter: drop-shadow(0 4px 14px rgba(0, 0, 0, 0.7));
}
</style>
